import { z } from 'zod'
import { widgetSchema } from '../../common/schema'
import { bookingParamsSchema } from './data'

// WP-style string booleans ("1"/"") on legacy keys the Booking Global
// Settings sheet renders (#1350, follow-up class from #859 / explorer's
// wpLenientBoolDefault in widgets/explorer/config.ts): a feed with an
// already-saved legacy string still fails the same strict z.boolean() way
// the Explorer checkboxes did before #1346. Duplicated locally rather than
// imported cross-widget, matching how the lenient* helper family is already
// per-widget-file in this codebase (see explorer/config.ts).
const wpLenientBoolDefault = (def: boolean) =>
  z.preprocess((v) => (
    typeof v === 'boolean' ? v
      : v === '1' || v === 1 || v === 'true' ? true
        : v === '' || v === '0' || v === 0 || v === 'false' ? false
          : undefined
  ), z.boolean().default(def))

// NO-DEFAULT variant for the priceBreakdown leaves below: those are
// deliberately optional-with-no-schema-default (see the feed-global-clobber
// comment on `priceBreakdown` — a materialized default would let a blank
// widget-level save clobber a real feed-global value through
// getFinalSettings). Tolerates the same WP legacy strings but degrades an
// unset/unparseable value to undefined, never to a materialized true/false.
const wpLenientBool = () =>
  z.preprocess((v) => (
    typeof v === 'boolean' ? v
      : v === '1' || v === 1 || v === 'true' ? true
        : v === '' || v === '0' || v === 0 || v === 'false' ? false
          : undefined
  ), z.boolean().optional())

export const bookingWidgetConfig = widgetSchema.extend({
  filter: bookingParamsSchema,
  calendarWidgetId: z.string().describe('Calendar widget to link with this booking widget: dates picked in that calendar fill the check-in and check-out here and stay in sync'),

  // Widget-specific options
  // #1350: rendered by the Booking Global Settings sheet (bookingGlobalSchema
  // picks this leaf) — wrapped for legacy WP-string tolerance, same class as
  // the Explorer checkboxes (#1346).
  showAvgNightPrice: wpLenientBoolDefault(true).describe('Show the average nightly price in the widget and the mobile floating bar'),
  // Toolkit: Per-Night Price Consistency (booking-widget-math-reconcile, #1064);
  // WP: price_with_dates = average_price ('total') | average_rent ('rent').
  nightlyPriceBasis: z.enum(['total', 'rent']).default('total').describe('How the per-night figure is computed. "total": the total the widget shows (after tax or pre-tax, per Show Total After Tax) divided by the nights, so nightly × nights equals that total. "rent": accommodation rent only divided by the nights (the WordPress plugin default), so fees and taxes sit outside the nightly number. Note: Explorer cards with Full Price quotes use the total basis; calendar-derived card prices are rent-only.'),
  // Toolkit: show_strikethrough (the module's one setting, default true).
  showNightlyStrikethrough: wpLenientBoolDefault(true).describe('When a discount applies, show the pre-discount per-night price struck through next to the active rate, in the widget and the mobile floating bar. Off shows the active rate only.'),
  // Toolkit: Original Total module (opt-in, no settings) — default off.
  showOriginalTotal: wpLenientBoolDefault(false).describe('When a discount applies, show the pre-discount total struck through beside the discounted total on the Total row. Rounding-only differences never strike. Off by default, like the toolkit Original Total module.'),
  showBedroomPrice: wpLenientBoolDefault(false).describe('Show a per-bedroom price hint (nightly price ÷ bedroom count) when the property has more than one bedroom'),
  perBedroomText: z.string().default('Only {price} per bedroom!').describe('Per-bedroom hint text; {price} is replaced with the per-bedroom amount'),
  initialCheckInDate: z.string().optional().describe('Initial check-in date (ISO string)'),
  initialCheckOutDate: z.string().optional().describe('Initial check-out date (ISO string)'),
  // min(1): the runtime reads this with `??`, so a stored 0 would preselect 0
  // guests — a dead value (#814). Unset means "use Default Number of Guests".
  initialGuests: z.number().int().min(1).optional().describe('Fixed pre-selected guest count when the widget loads. Takes priority over Default Number of Guests.'),
  guestBreakdown: wpLenientBoolDefault(false).describe('Break the guest picker into separate Adults and Children counts (plus optional Pets and Infants rows) instead of a single guest total'),
  showPets: wpLenientBoolDefault(true).describe('Show a Pets row in the guest breakdown picker and send the pets count with quotes. On properties that don\'t allow pets the row is disabled with a "Not allowed" note.'),
  showInfants: wpLenientBoolDefault(false).describe('Show an Infants row in the guest breakdown picker and send the infants count with quotes. Leave off unless your PMS or an integration uses infant counts.'),
  // Toolkit guest-picker `pet_notice_enabled` / `pet_notice_text` (#1230).
  // Display-only note under the Pets row, shown whenever pets > 0 (any count,
  // not only at the cap — toolkit guest-picker.js parity). Off by default and
  // the text mirrors the toolkit default verbatim, so a fresh site matches.
  // #1350: NOT wrapped — bookingGlobalSchema does not pick this leaf, so the
  // Booking Global Settings sheet never renders it (stays strict; per-widget
  // editor only, out of this issue's read-side scope).
  showPetNotice: z.boolean().default(false).describe('Show a short note under the Pets row whenever at least one pet is selected (for example, to warn that pet fees apply). Requires the Pets row.'),
  // No length cap: render-time parsing is strict (#1168 class), so a cap here would
  // brick a widget whose stored text outgrew it. The toolkit stores it uncapped too.
  petNoticeText: z.string().default('Pet fees may apply and will be reflected in your quote.').describe('Text of the pet notice'),
  // At-cap note (#1230 follow-up; toolkit guest-picker.js:242-254
  // showPetsTooltip): shown when a guest tries to add more pets than a
  // per-property max_pets ceiling allows. Not toggleable like showPetNotice —
  // it's tied to a real constraint, not an optional add-on, so hiding it would
  // reintroduce silent clamping. Singular gets its own string; "{count}" in
  // the plural string is replaced with the property's max_pets.
  maxPetsNoticeSingular: z.string().default('This property allows a maximum of 1 pet').describe('Note shown when a guest tries to add more pets than the property allows, when that maximum is exactly 1'),
  maxPetsNoticeText: z.string().default('This property allows a maximum of {count} pets').describe('Note shown when a guest tries to add more pets than the property allows (2 or more); {count} is replaced with the property\'s pets maximum'),
  // Feed-level pets-cap fallback (#1407, follow-up to #1230/#1405; toolkit
  // parity: guest-picker's account-wide `max_pets` admin setting,
  // Admin/GuestPicker/Page.php DEFAULTS['max_pets']). Resolve order matches
  // the toolkit's own (GuestPicker.php:88-100): a property's own per-property
  // `max_pets` (PMS sync) wins when present, else this feed-level setting,
  // else the built-in PETS_MAX(5) fallback. Unset means "no feed-level cap" —
  // identical PETS_MAX-only behavior as before this issue. The at-cap note
  // above fires against this cap too, same copy as the per-property cap.
  // min(1): same rationale as initialGuests above — a stored 0 would read as
  // "no pets allowed," a dead and confusing value for a "cap" field.
  maxPetsDefault: z.number().int().min(1).optional().describe('Fallback pets ceiling used only when the property itself carries no per-property pets maximum. Leave unset to use the built-in default (5).'),
  defaultGuests: z.enum(['min', 'base', 'max']).optional().describe('Which guest count is preselected when the widget loads: the minimum (1), the property’s base guest count, or its maximum. Ignored when Initial Guests is set; if unset, 1 is preselected.'),

  // Display toggles (WP plugin parity — defaults preserve existing behavior)
  showMinStay: wpLenientBoolDefault(true).describe('Show the minimum-stay info before dates are selected'),
  showDatepicker: wpLenientBoolDefault(true).describe('Show the check-in/check-out date inputs'),
  // #1350: NOT wrapped — bookingGlobalSchema does not pick this leaf, so the
  // Booking Global Settings sheet never renders it (stays strict).
  showSelectedDates: z.boolean().default(false).describe('Show the selected dates as read-only text when the datepicker is hidden'),
  showCoupon: wpLenientBoolDefault(true).describe('Show the coupon code section'),
  couponCode: z.string().optional().describe('Coupon code automatically applied to every quote'),
  showCheckoutButton: wpLenientBoolDefault(true).describe('Show the booking/checkout button'),
  // Default `true`: most stored booking widgets carry no totalAfterTax key
  // and get their post-tax behavior from this parse-time default. Fresh
  // widgets used to be seeded `false` (pre-tax, WP parity) via the
  // dashboard's FRESH_WIDGET_SETTING_OVERRIDES (#685 decision 2B); that seed
  // was retired #1062 (Eli 2026-09-07), so fresh widgets now inherit this
  // same `true` default too — no dashboard override needed.
  totalAfterTax: wpLenientBoolDefault(true).describe('Show the total including taxes; otherwise show the pre-tax total'),
  showPriceDetails: wpLenientBoolDefault(true).describe('Show the button that opens the price breakdown'),
  // WP: details_btn_text
  detailsButtonText: z.string().default('Show Price Details').describe('Label for the button that opens the price breakdown'),
  // WP: show_subtotal
  showSubtotal: wpLenientBoolDefault(true).describe('Show the Subtotal row inside the price breakdown'),
  // WP: price_details_text_replacements
  // #1191: optional with NO schema default, and both leaves optional.
  //  - `.default([])` materialized `[]` into every saved widget, and the #743
  //    atomic-array merge in getFinalSettings let that `[]` wipe the
  //    feed-global list at render (feed-global-clobber rule). Absent key =
  //    inherit (feed global, else no replacements).
  //  - Optional leaves: the dashboard add-row seeds `{}`, and
  //    bookingGlobalSchema (deepPartial) already stores partial rows; a
  //    required from/to made either shape brick every booking widget at
  //    parse time (#1168 class). Rows missing `from` never match a line item;
  //    rows missing `to` relabel to the original title.
  priceLabelReplacements: z.array(z.object({
    from: z.string().optional().describe('Original price-detail label (exact match)'),
    to: z.string().optional().describe('Replacement label'),
  })).optional().describe('Rename line items in the price breakdown. Add a rule per label, e.g. From "Accommodation fare" → To "Nightly rate".'),
  // Toolkit: Fee Line Separator (fee-line-separator, #1064 Wave 2). Raw shape
  // only — defaulting, gating, live-inherit, and normalization all live in
  // fee-line-separator.ts's `resolveFeeLines()` docblock (feed-global-clobber
  // rule: every leaf here is optional with no schema default).
  feeLines: z.object({
    names: z.array(z.string()).optional().describe('Fee names to pull onto their own line (matched against the fee\'s title). Empty/absent = nothing configured at this level; a widget with no Fee Lines config of its own follows the feed-level one live.'),
    matchMode: z.enum(['contains', 'exact']).optional().describe('How a Name is matched against a fee\'s title. Case-insensitive, trims a trailing colon and extra whitespace. Absent = "contains".'),
    relabel: z.array(z.object({
      from: z.string().optional().describe('Original fee title to rename (full title, case-insensitive). Note: with "contains" matching, this is the full title of the matched fee, not the Names entry.'),
      to: z.string().optional().describe('Display title for the separated row'),
    })).optional().describe('Rename a separated fee\'s displayed title, e.g. From "Cleaning Fee" → To "Housekeeping".'),
    combinedLabel: z.string().optional().describe('Label for the combined remainder line; absent = "Fees:". Omitted entirely when the remainder rounds to $0.00.'),
    position: z.enum(['before', 'after']).optional().describe('Separated fee rows before or after the combined line; absent = "before"'),
    mode: z.enum(['combined', 'rent']).optional().describe('"combined" (absent/default): the rest of the fees fold into one labeled line — selected with nothing else configured it is treated as not configured, so it doubles as the Select\'s unset position. "rent": they fold into the accommodation/rent line instead — no combined line, the nightly "rent" price basis reconciles to match, and on its own it folds EVERY fee. Falls back to "combined" when there is no rent line to fold into.'),
  }).optional().describe('Fee Line Separator: keep named fees on their own line in the price breakdown; the rest combine into one line (or fold into the rent line)'),
  // Toolkit: Inline Price Breakdown (inline-price-breakdown, #1064 Wave 3).
  // Raw shape only — defaulting, the enabled gate, and normalization live in
  // common/price-breakdown/breakdown.ts's `resolvePriceBreakdown()` docblock
  // (feed-global-clobber rule: every leaf optional, no schema defaults; the
  // save-path half is `stripBlankPriceBreakdown` in widget-settings-prep.ts).
  priceBreakdown: z.object({
    // #1350: every boolean leaf in this group is rendered by the Booking
    // Global Settings sheet (bookingGlobalSchema picks `priceBreakdown`
    // whole) — wrapped with the NO-DEFAULT wpLenientBool, not
    // wpLenientBoolDefault, so the feed-global-clobber contract below
    // (optional, no schema default) holds exactly as before.
    enabled: wpLenientBool().describe('Show the itemized price breakdown inline in the booking widget, above or below the Total row. Off/absent = no inline breakdown (the Price Details modal alone, today\'s behavior). A widget-level Off overrides a feed-level On.'),
    style: z.enum(['visible', 'accordion']).optional().describe('"visible" (absent/default): the rows always show. "accordion": the rows sit behind an expandable toggle that repeats the total while collapsed.'),
    grouping: z.enum(['itemized', 'grouped']).optional().describe('"itemized" (absent/default): one row per charge. "grouped": one summed row per group (Rent / Fees / Add-ons / Others / Taxes).'),
    rentStyle: z.enum(['perNight', 'title']).optional().describe('Rent row label. "perNight" (absent/default): "$68 × 3 nights". "title": the charge\'s own name, e.g. "Accommodation fare".'),
    showTaxes: wpLenientBool().describe('Show tax rows in the inline breakdown. Absent/off = taxes stay out of the inline rows (the guest still sees them at checkout and in the Price Details modal).'),
    hideDeposit: wpLenientBool().describe('Hide the refundable security-deposit row. Absent/off = shown (the WP module default); the polarity is deliberate so an untouched checkbox reads the same as the runtime default.'),
    position: z.enum(['above', 'below']).optional().describe('Where the breakdown sits relative to the widget\'s Total row; absent = "above".'),
    hideDetailsButton: wpLenientBool().describe('Hide the Price Details modal button while the inline breakdown is on, to avoid two price surfaces.'),
    heading: z.string().optional().describe('Optional heading above the always-visible breakdown. Ignored in accordion style (the toggle is the heading there).'),
    accordionLabel: z.string().optional().describe('Accordion toggle label, used verbatim in BOTH states (e.g. "What you pay"). Absent = state-reflecting "Show/Hide price breakdown".'),
    openByDefault: wpLenientBool().describe('Accordion starts open instead of collapsed. Once the guest toggles it, their choice wins for the rest of the visit.'),
    taxDisplay: z.enum(['itemized', 'grouped']).optional().describe('"itemized" (absent/default): one row per tax. "grouped": one summed Taxes row with the individual taxes behind a disclosure — useful in markets that stack 3+ taxes.'),
    taxLabel: z.string().optional().describe('Label for the grouped Taxes row; absent = "Taxes".'),
    explanations: z.array(z.object({
      from: z.string().optional().describe('Fee name (or fragment) to explain, matched against the charge\'s title — case-insensitive, trailing colon ignored, exact match first then contains'),
      to: z.string().optional().describe('Explanation shown when the guest taps the "i" next to that charge'),
    })).optional().describe('Guest-facing explanations for individual charges, e.g. From "Damage waiver" → "Replaces a refundable deposit and covers accidental damage up to $500."'),
  }).optional().describe('Inline Price Breakdown: itemized charges rendered directly in the booking widget next to the Total, with optional accordion, grouped taxes, and per-fee explanations'),
  // Toolkit: Rate Plan Control (rate-plan-control 3.235.0 rename + explainer,
  // 3.258.0 hide-on-coupon; #1690). Raw shape only — normalization, the
  // inert-when-unset gate, and the apply rules live in rate-plan-control.ts.
  // Every leaf optional with no schema default (feed-global-clobber rule; a
  // partial row must parse, #1168 class).
  ratePlanControl: z.object({
    rules: z.array(z.object({
      match: z.string().optional().describe('Text matched against the rate plan\'s original title (case-insensitive). Rows with no match text are ignored; the first matching row wins.'),
      matchType: z.enum(['contains', 'startsWith', 'exact']).optional().describe('How Match is compared to the plan title. Absent = "contains".'),
      display: z.string().optional().describe('Name shown to guests instead of the plan\'s title. Blank keeps the original.'),
      tooltip: z.string().optional().describe('Explainer shown on hover behind an info icon next to the plan name (for example, the cancellation policy). Blank = no icon.'),
      hideWithCoupon: wpLenientBool().describe('Hide this plan once a coupon is applied to the quote, so a promo code never stacks on the plan\'s own discount. If that would hide every plan, none is hidden.'),
      hiddenNoteEnabled: wpLenientBool().describe('Tell the guest why a plan was hidden. Absent = on.'),
      hiddenNote: z.string().optional().describe('Custom guest note; {hidden} and {remaining} are replaced with the plan names. Blank = the default text.'),
    })).optional().describe('Rate Plan Control rows'),
  }).optional().describe('Rate Plan Control: rename rate plans, add a hover explainer, and hide a plan once a coupon applies. Unset = plans render as the PMS names them.'),
  // WP: date_format
  dateFormat: z.string().default('MMM d, yyyy').describe('Luxon format for displayed dates, e.g. "MMM d, yyyy" or "LLLL d, yyyy"'),
  // WP: btn_text_inquiry / btn_text_request / btn_text_instant
  buttonText: z.object({
    inquiry: z.string().default('Get a Quote').describe('Button label when the property uses the inquiry reservation method'),
    request: z.string().default('Send Request').describe('Button label for the request reservation method'),
    instant: z.string().default('Instant Booking').describe('Button label for the instant-booking reservation method'),
    fallback: z.string().default('Book Now').describe('Button label when the reservation method is unknown'),
  }).default({ inquiry: 'Get a Quote', request: 'Send Request', instant: 'Instant Booking', fallback: 'Book Now' }).describe('Booking button text per reservation method'),
  // WP: sticky (default on), sticky_class, sticky_btn_class
  // #1350: NOT wrapped — bookingGlobalSchema does not pick `sticky`, so the
  // Booking Global Settings sheet never renders sticky.enabled (stays strict).
  sticky: z.object({
    enabled: z.boolean().default(true).describe('Show the floating booking bar on mobile (below 960px). It replaces the in-page widget and opens the calendar and booking form in modals'),
    className: z.string().optional().describe('Extra CSS classes for the floating bar container'),
    buttonClassName: z.string().optional().describe('Extra CSS classes for the floating bar button'),
  }).default({ enabled: true }),

  // #1424: the guest-facing strings BookingForm.tsx used to render as bare
  // JSX literals — the booking widget was the only guest-facing widget with
  // no translation layer (found while #1062 item 1 had to ship "Total before
  // taxes" / "Taxes added at checkout" as English-only literals to keep that
  // fix label-only). Same shape and resolution order as every other widget's
  // `translations` group (common/i18n): operator value for the visitor locale
  // → operator value for another locale of the same language → the shipped
  // locale table (configured locale only) → this en_US default. Fields
  // ALREADY operator-configurable as a single string (buttonText.*,
  // perBedroomText, detailsButtonText, petNoticeText, maxPetsNoticeSingular,
  // maxPetsNoticeText, priceLabelReplacements, feeLines/priceBreakdown labels)
  // are a separate, pre-existing mechanism and stay out of this table —
  // converting those is its own follow-up, not this issue's scope.
  translations: z.object({
    // Notices (transient status text set on quoteNotice/couponNotice)
    notice_select_dates: z.record(z.string(), z.string()).default({
      en_US: 'Please select dates',
    }).describe('Notice shown on Checkout when no dates are selected and the datepicker is hidden'),
    notice_select_guests: z.record(z.string(), z.string()).default({
      en_US: 'Please select guests',
    }).describe('Notice shown on Checkout when no guests are selected'),
    notice_hosted_checkout_failed: z.record(z.string(), z.string()).default({
      en_US: 'Could not open the booking page. Please try again.',
    }).describe('Notice shown when the external PMS hosted-checkout URL fails to resolve'),
    notice_generic_error: z.record(z.string(), z.string()).default({
      en_US: 'An error occurred',
    }).describe('Fallback notice for an unhandled checkout error, and the humanizeError fallback for a quote failure that is not a coupon rejection'),
    notice_coupon_failed: z.record(z.string(), z.string()).default({
      en_US: 'Coupon could not be applied',
    }).describe('humanizeError fallback shown when an applied coupon causes the quote to fail with an unrecognized error'),
    notice_coupon_needs_dates: z.record(z.string(), z.string()).default({
      en_US: 'Please select dates and guests first',
    }).describe('Notice shown when Apply is pressed with no active quote yet'),
    notice_coupon_needs_code: z.record(z.string(), z.string()).default({
      en_US: 'Please enter a coupon code',
    }).describe('Notice shown when Apply is pressed with a blank coupon field'),
    notice_coupon_applied: z.record(z.string(), z.string()).default({
      en_US: 'Coupon applied!',
    }).describe('Notice shown when the quote honored the applied coupon'),
    notice_coupon_invalid: z.record(z.string(), z.string()).default({
      en_US: 'Coupon not valid for these dates',
    }).describe('Notice shown when the quote did not honor the applied coupon'),

    // Buttons / CTA states
    button_processing: z.record(z.string(), z.string()).default({
      en_US: 'Processing...',
    }).describe('Checkout button text while a quote settles or checkout is loading'),
    button_check_availability: z.record(z.string(), z.string()).default({
      en_US: 'Check availability',
    }).describe('Checkout button text before dates are selected'),
    button_close: z.record(z.string(), z.string()).default({
      en_US: 'Close',
    }).describe('Close button in the guest picker panel'),
    button_coupon_apply: z.record(z.string(), z.string()).default({
      en_US: 'Apply',
    }).describe('Coupon Apply button text'),
    button_coupon_applying: z.record(z.string(), z.string()).default({
      en_US: 'Applying…',
    }).describe('Coupon Apply button text while a coupon re-quote is in flight'),

    // Date fields
    label_check_in: z.record(z.string(), z.string()).default({
      en_US: 'Check in',
    }).describe('Check-in date field label'),
    label_check_out: z.record(z.string(), z.string()).default({
      en_US: 'Check out',
    }).describe('Check-out date field label'),
    label_check_in_static: z.record(z.string(), z.string()).default({
      en_US: 'Check In',
    }).describe('Check-in label shown when the datepicker is hidden and Show Selected Dates is on'),
    label_check_out_static: z.record(z.string(), z.string()).default({
      en_US: 'Check Out',
    }).describe('Check-out label shown when the datepicker is hidden and Show Selected Dates is on'),
    placeholder_add_date: z.record(z.string(), z.string()).default({
      en_US: 'Add date',
    }).describe('Placeholder text in the empty check-in/check-out date inputs'),

    // Guest picker
    label_guests: z.record(z.string(), z.string()).default({
      en_US: 'Guests',
    }).describe('Guests field label (collapsed field and single-total picker row)'),
    label_adults: z.record(z.string(), z.string()).default({
      en_US: 'Adults',
    }).describe('Adults row label (guest breakdown on)'),
    label_children: z.record(z.string(), z.string()).default({
      en_US: 'Children',
    }).describe('Children row label (guest breakdown on)'),
    label_infants: z.record(z.string(), z.string()).default({
      en_US: 'Infants',
    }).describe('Infants row label (guest breakdown + Show Infants on)'),
    label_pets: z.record(z.string(), z.string()).default({
      en_US: 'Pets',
    }).describe('Pets row label (guest breakdown + Show Pets on)'),
    hint_adults_age: z.record(z.string(), z.string()).default({
      en_US: 'Age 13+',
    }).describe('Age hint under the Adults row'),
    hint_children_age: z.record(z.string(), z.string()).default({
      en_US: 'Ages 2–12',
    }).describe('Age hint under the Children row'),
    hint_infants_age: z.record(z.string(), z.string()).default({
      en_US: 'Under 2',
    }).describe('Age hint under the Infants row'),
    hint_pets_not_allowed: z.record(z.string(), z.string()).default({
      en_US: 'Not allowed at this property',
    }).describe('Hint shown next to the Pets row when the property disallows pets'),
    aria_guest_selection: z.record(z.string(), z.string()).default({
      en_US: 'Guest selection',
    }).describe('Screen-reader label on the guest picker panel'),
    aria_decrease_guests: z.record(z.string(), z.string()).default({
      en_US: 'Decrease guests',
    }).describe('Screen-reader label on the Guests stepper minus button'),
    aria_increase_guests: z.record(z.string(), z.string()).default({
      en_US: 'Increase guests',
    }).describe('Screen-reader label on the Guests stepper plus button'),
    aria_decrease_adults: z.record(z.string(), z.string()).default({
      en_US: 'Decrease adults',
    }).describe('Screen-reader label on the Adults stepper minus button'),
    aria_increase_adults: z.record(z.string(), z.string()).default({
      en_US: 'Increase adults',
    }).describe('Screen-reader label on the Adults stepper plus button'),
    aria_decrease_children: z.record(z.string(), z.string()).default({
      en_US: 'Decrease children',
    }).describe('Screen-reader label on the Children stepper minus button'),
    aria_increase_children: z.record(z.string(), z.string()).default({
      en_US: 'Increase children',
    }).describe('Screen-reader label on the Children stepper plus button'),
    aria_decrease_infants: z.record(z.string(), z.string()).default({
      en_US: 'Decrease infants',
    }).describe('Screen-reader label on the Infants stepper minus button'),
    aria_increase_infants: z.record(z.string(), z.string()).default({
      en_US: 'Increase infants',
    }).describe('Screen-reader label on the Infants stepper plus button'),
    aria_decrease_pets: z.record(z.string(), z.string()).default({
      en_US: 'Decrease pets',
    }).describe('Screen-reader label on the Pets stepper minus button'),
    aria_increase_pets: z.record(z.string(), z.string()).default({
      en_US: 'Increase pets',
    }).describe('Screen-reader label on the Pets stepper plus button'),
    unit_guest_one: z.record(z.string(), z.string()).default({
      en_US: 'guest',
    }).describe('Unit word after the guest count when it is 1'),
    unit_guest_other: z.record(z.string(), z.string()).default({
      en_US: 'guests',
    }).describe('Unit word after the guest count'),
    unit_infant_one: z.record(z.string(), z.string()).default({
      en_US: 'infant',
    }).describe('Unit word after the infant count when it is 1'),
    unit_infant_other: z.record(z.string(), z.string()).default({
      en_US: 'infants',
    }).describe('Unit word after the infant count'),
    unit_pet_one: z.record(z.string(), z.string()).default({
      en_US: 'pet',
    }).describe('Unit word after the pet count when it is 1'),
    unit_pet_other: z.record(z.string(), z.string()).default({
      en_US: 'pets',
    }).describe('Unit word after the pet count'),
    guest_max_note: z.record(z.string(), z.string()).default({
      en_US: 'This place has a maximum of {{n}} {{unit}}.',
    }).describe('Note under the guest picker naming the property\'s guest maximum ({{n}} = count, {{unit}} = the guest unit word)'),
    guest_max_note_with_infants: z.record(z.string(), z.string()).default({
      en_US: 'This place has a maximum of {{n}} {{unit}}, not including infants.',
    }).describe('Same note when the Infants row is shown (infants excluded from the guest maximum)'),

    // Nights / stay
    unit_night_one: z.record(z.string(), z.string()).default({
      en_US: 'night',
    }).describe('Unit word after the night count when it is 1'),
    unit_night_other: z.record(z.string(), z.string()).default({
      en_US: 'nights',
    }).describe('Unit word after the night count'),
    label_min_stay: z.record(z.string(), z.string()).default({
      en_US: 'Minimum stay: {{value}}',
    }).describe('Minimum-stay line before dates are selected ({{value}} = the composed count + unit)'),
    pill_min_stay: z.record(z.string(), z.string()).default({
      en_US: '{{n}}-night min',
    }).describe('Compact stay-pill text before dates are selected ({{n}} = the minimum-stay count)'),
    pill_min_stay_one: z.record(z.string(), z.string()).default({
      en_US: '1-night min',
    }).describe('Singular stay-pill text for a one-night minimum (languages where the plural form is ungrammatical with 1)'),

    // Coupon
    label_have_coupon: z.record(z.string(), z.string()).default({
      en_US: 'I have a coupon',
    }).describe('Checkbox label that reveals the coupon input'),
    placeholder_coupon_code: z.record(z.string(), z.string()).default({
      en_US: 'Coupon code',
    }).describe('Placeholder text in the coupon code input'),

    // Total row
    label_total_after_tax: z.record(z.string(), z.string()).default({
      en_US: 'Total after tax',
    }).describe('Total row label when Show Total After Tax is on'),
    label_total_before_taxes: z.record(z.string(), z.string()).default({
      en_US: 'Total before taxes',
    }).describe('Total row label when Show Total After Tax is off (#1062 item 1)'),
    note_taxes_at_checkout: z.record(z.string(), z.string()).default({
      en_US: 'Taxes added at checkout',
    }).describe('Muted note under the pre-tax Total row (#1062 item 1)'),

    // Discount banner
    discount_weekly_and_monthly: z.record(z.string(), z.string()).default({
      en_US: 'Save {{weekly}}% on weekly and {{monthly}}% on monthly stays!',
    }).describe('Discount note when both a weekly and a monthly discount apply'),
    discount_weekly: z.record(z.string(), z.string()).default({
      en_US: 'Save {{weekly}}% on weekly stays!',
    }).describe('Discount note when only a weekly discount applies'),
    discount_monthly: z.record(z.string(), z.string()).default({
      en_US: 'Save {{monthly}}% on monthly stays!',
    }).describe('Discount note when only a monthly discount applies'),

    // Modal (widget modal)
    dialog_title: z.record(z.string(), z.string()).default({
      en_US: 'Booking',
    }).describe('Accessible title of the floating-bar\'s full-form modal'),

    // Sticky bar (StickyBar.tsx) — the issue's claim that the floating bar
    // "renders only the formatted number and needs no change" missed these:
    // the bar also renders its own surrounding words, which must not stay
    // English on a Spanish-configured site.
    label_starting_at: z.record(z.string(), z.string()).default({
      en_US: 'Starting at ',
    }).describe('Word before the no-dates-yet rent figure (sticky bar and the in-page card)'),
    unit_per_night: z.record(z.string(), z.string()).default({
      en_US: ' / night',
    }).describe('Unit after a nightly price figure (sticky bar and the in-page card)'),
    unit_total: z.record(z.string(), z.string()).default({
      en_US: ' total',
    }).describe('Word after the quoted total in the sticky bar\'s price line'),
    aria_loading_price: z.record(z.string(), z.string()).default({
      en_US: 'Loading price',
    }).describe('Screen-reader label on the sticky bar\'s loading skeleton'),

    // Calendar modal (CalendarModal.tsx)
    calendar_modal_title: z.record(z.string(), z.string()).default({
      en_US: 'Select dates',
    }).describe('Accessible title of the date-picker modal'),
    label_when: z.record(z.string(), z.string()).default({
      en_US: 'When',
    }).describe('Header text in the date-picker modal'),
    button_clear_dates: z.record(z.string(), z.string()).default({
      en_US: 'Clear Dates',
    }).describe('Clear Dates button in the date-picker modal footer'),

    // Price details modal (PriceDetailsModal.tsx)
    label_subtotal: z.record(z.string(), z.string()).default({
      en_US: 'Subtotal:',
    }).describe('Subtotal row label in the price details modal'),
    label_total_modal: z.record(z.string(), z.string()).default({
      en_US: 'Total:',
    }).describe('Total row label in the price details modal'),
    aria_charge_explanation: z.record(z.string(), z.string()).default({
      en_US: 'About this charge',
    }).describe('Screen-reader label on a charge\'s explanation-toggle button in the price details modal'),
    aria_price_details_dialog: z.record(z.string(), z.string()).default({
      en_US: 'Price details',
    }).describe('Screen-reader name of the price-details dialog'),
    aria_close_price_details: z.record(z.string(), z.string()).default({
      en_US: 'Close price details',
    }).describe('Screen-reader label on the price details modal\'s close button'),
    // #1530 follow-up (Rentus, 2026-09-15): the bucket group labels the price
    // details modal and the inline grouped breakdown print. The shared
    // common/price-breakdown BUCKET_GROUP_LABELS stay the en_US defaults.
    group_rent: z.record(z.string(), z.string()).default({
      en_US: 'Rent:',
    }).describe('Price breakdown: group label for the rent charge(s)'),
    group_fees: z.record(z.string(), z.string()).default({
      en_US: 'Fee(s):',
    }).describe('Price breakdown: group label for fees'),
    group_addons: z.record(z.string(), z.string()).default({
      en_US: 'Add-on(s):',
    }).describe('Price breakdown: group label for add-ons'),
    group_others: z.record(z.string(), z.string()).default({
      en_US: 'Others:',
    }).describe('Price breakdown: group label for refundable and other charges'),
    group_taxes: z.record(z.string(), z.string()).default({
      en_US: 'Tax(es):',
    }).describe('Price breakdown: group label for taxes'),
  }).default({
    notice_select_dates: { en_US: 'Please select dates' },
    notice_select_guests: { en_US: 'Please select guests' },
    notice_hosted_checkout_failed: { en_US: 'Could not open the booking page. Please try again.' },
    notice_generic_error: { en_US: 'An error occurred' },
    notice_coupon_failed: { en_US: 'Coupon could not be applied' },
    notice_coupon_needs_dates: { en_US: 'Please select dates and guests first' },
    notice_coupon_needs_code: { en_US: 'Please enter a coupon code' },
    notice_coupon_applied: { en_US: 'Coupon applied!' },
    notice_coupon_invalid: { en_US: 'Coupon not valid for these dates' },
    button_processing: { en_US: 'Processing...' },
    button_check_availability: { en_US: 'Check availability' },
    button_close: { en_US: 'Close' },
    button_coupon_apply: { en_US: 'Apply' },
    button_coupon_applying: { en_US: 'Applying…' },
    label_check_in: { en_US: 'Check in' },
    label_check_out: { en_US: 'Check out' },
    label_check_in_static: { en_US: 'Check In' },
    label_check_out_static: { en_US: 'Check Out' },
    placeholder_add_date: { en_US: 'Add date' },
    label_guests: { en_US: 'Guests' },
    label_adults: { en_US: 'Adults' },
    label_children: { en_US: 'Children' },
    label_infants: { en_US: 'Infants' },
    label_pets: { en_US: 'Pets' },
    hint_adults_age: { en_US: 'Age 13+' },
    hint_children_age: { en_US: 'Ages 2–12' },
    hint_infants_age: { en_US: 'Under 2' },
    hint_pets_not_allowed: { en_US: 'Not allowed at this property' },
    aria_guest_selection: { en_US: 'Guest selection' },
    aria_decrease_guests: { en_US: 'Decrease guests' },
    aria_increase_guests: { en_US: 'Increase guests' },
    aria_decrease_adults: { en_US: 'Decrease adults' },
    aria_increase_adults: { en_US: 'Increase adults' },
    aria_decrease_children: { en_US: 'Decrease children' },
    aria_increase_children: { en_US: 'Increase children' },
    aria_decrease_infants: { en_US: 'Decrease infants' },
    aria_increase_infants: { en_US: 'Increase infants' },
    aria_decrease_pets: { en_US: 'Decrease pets' },
    aria_increase_pets: { en_US: 'Increase pets' },
    unit_guest_one: { en_US: 'guest' },
    unit_guest_other: { en_US: 'guests' },
    unit_infant_one: { en_US: 'infant' },
    unit_infant_other: { en_US: 'infants' },
    unit_pet_one: { en_US: 'pet' },
    unit_pet_other: { en_US: 'pets' },
    guest_max_note: { en_US: 'This place has a maximum of {{n}} {{unit}}.' },
    guest_max_note_with_infants: { en_US: 'This place has a maximum of {{n}} {{unit}}, not including infants.' },
    unit_night_one: { en_US: 'night' },
    unit_night_other: { en_US: 'nights' },
    label_min_stay: { en_US: 'Minimum stay: {{value}}' },
    pill_min_stay: { en_US: '{{n}}-night min' },
    pill_min_stay_one: { en_US: '1-night min' },
    label_have_coupon: { en_US: 'I have a coupon' },
    placeholder_coupon_code: { en_US: 'Coupon code' },
    label_total_after_tax: { en_US: 'Total after tax' },
    label_total_before_taxes: { en_US: 'Total before taxes' },
    note_taxes_at_checkout: { en_US: 'Taxes added at checkout' },
    discount_weekly_and_monthly: { en_US: 'Save {{weekly}}% on weekly and {{monthly}}% on monthly stays!' },
    discount_weekly: { en_US: 'Save {{weekly}}% on weekly stays!' },
    discount_monthly: { en_US: 'Save {{monthly}}% on monthly stays!' },
    dialog_title: { en_US: 'Booking' },
    label_starting_at: { en_US: 'Starting at ' },
    unit_per_night: { en_US: ' / night' },
    unit_total: { en_US: ' total' },
    aria_loading_price: { en_US: 'Loading price' },
    calendar_modal_title: { en_US: 'Select dates' },
    label_when: { en_US: 'When' },
    button_clear_dates: { en_US: 'Clear Dates' },
    label_subtotal: { en_US: 'Subtotal:' },
    label_total_modal: { en_US: 'Total:' },
    aria_charge_explanation: { en_US: 'About this charge' },
    aria_price_details_dialog: { en_US: 'Price details' },
    aria_close_price_details: { en_US: 'Close price details' },
    group_rent: { en_US: 'Rent:' },
    group_fees: { en_US: 'Fee(s):' },
    group_addons: { en_US: 'Add-on(s):' },
    group_others: { en_US: 'Others:' },
    group_taxes: { en_US: 'Tax(es):' },
  }).describe('The translations of the widget'),
})

export type BookingWidgetConfig = z.infer<typeof bookingWidgetConfig>

/** Convenience alias for the child components (StickyBar, CalendarModal,
 *  PriceDetailsModal) that receive `_t` as a prop instead of calling
 *  useLocalizedTranslation themselves — they only get pre-formatted display
 *  strings from BookingForm.tsx otherwise, not the raw widget options. */
export type BookingTranslationKey = keyof BookingWidgetConfig['translations']

// Feed-wide GLOBAL Booking defaults (#437 section A). A curated subset of the
// per-widget config that can be set once on the feed (stored at
// feed.additional_info.booking) and inherited by every Booking widget instance
// unless that instance overrides it. Inheritance order at render:
// widget setting → feed global default → widget schema default. Every field is
// optional (absent = no global default → fall back to the schema default).
// Mirrors the Explorer global pattern (explorerGlobalSchema).
export const bookingGlobalSchema = bookingWidgetConfig
  .pick({
    showAvgNightPrice: true,
    nightlyPriceBasis: true,
    showNightlyStrikethrough: true,
    showOriginalTotal: true,
    showBedroomPrice: true,
    perBedroomText: true,
    totalAfterTax: true,
    showPriceDetails: true,
    detailsButtonText: true,
    showSubtotal: true,
    priceLabelReplacements: true,
    feeLines: true,
    priceBreakdown: true,
    dateFormat: true,
    buttonText: true,
    showMinStay: true,
    showDatepicker: true,
    showCoupon: true,
    showCheckoutButton: true,
    defaultGuests: true,
    guestBreakdown: true,
    showPets: true,
    showInfants: true,
  })
  .deepPartial()

export type BookingGlobalConfig = z.infer<typeof bookingGlobalSchema>

export type DefaultGuestsMode = 'min' | 'base' | 'max'

/**
 * Resolve the "Default number of Guests" setting to an actual guest count using
 * the property's guest bounds. Mirrors the WP plugin's Min/Base/Max Guests option.
 * Falls back to 1 for "min", an unknown/undefined mode, or missing property data.
 */
export function resolveDefaultGuests(
  mode: DefaultGuestsMode | undefined,
  property: { base_guest_count?: number | null; maximum_guest_count?: number | null } | null | undefined,
): number {
  if (mode === 'base') return property?.base_guest_count || 1
  if (mode === 'max') return property?.maximum_guest_count || 1
  return 1
}
